import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import StyledLoading from '../../app/components/loading/StyledLoading';
import SeoHead from '../../app/components/SeoHead';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatDateTime, timeAgo, timeAgo2 } from '../../utils/helper';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import YoutubeIcon from '../../app/components/svgIcons/YoutubeIcon';

const Card = styled.div`
  border-radius: 5px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
`;

export const StyledTooltip = styled(Tooltip)`
  position: absolute !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  .tooltip-arrow {
    // display: none !important;
  }

  .tooltip-inner {
    background-color: ${({ theme }) => theme?.dark40} !important;
    border-radius: 2px;
    padding: 4px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.06), 0 8px 16px 0 rgba(0, 0, 0, 0.1);
    font-size: 12px;
    font-family: ${({ theme }) => theme?.fontFamily} !important;
    overflow: visible;
    max-width: 300px;
  }
`;

const StyledHeading = styled.h3`
  font-size: 50px;
  letter-spacing: 4px;
  color: #cb1d8d;

  @media (max-width: 768px) {
    font-size: 35px;
  }
`;

const seoData = {
  title: 'Watch BoostFloral on YouTube | Floral Business Tips & Tutorials',
  description:
    'Explore our YouTube channel for powerful floral business tutorials, eCommerce growth tips, website design guides, and digital marketing strategies for florists. Subscribe for weekly insights!',
  keywords:
    'BoostFloral YouTube, Floral business YouTube channel, Flower shop marketing, Florist video tutorials, Website design for florists, Floral eCommerce YouTube, Digital marketing florists, Flower shop SEO, Online florist strategies, Floral software tutorials, Florist how-to videos, Florist video content, Grow flower shop online, YouTube florists, YouTube for flower shops',
  ogTitle: 'Watch BoostFloral on YouTube | Floral Website & Business Tips',
  ogDescription:
    'Subscribe to the BoostFloral YouTube channel for tutorials, tips, and strategies to grow your online flower shop. Get inspired and learn how to stand out with smart tech!',
  ogType: 'video.other',
  ogSiteName: 'BoostFloral',
  ogImage: 'https://www.boostfloral.com/logo.svg',
  ogUrl: 'https://www.youtube.com/@BoostFloral',
};

const CHANNEL_ID = 'UCFFJOqfAGjvg2POjBk0mMWg';
const RSS_URL = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`;
const PROXY_URL = `https://api.allorigins.win/raw?url=${encodeURIComponent(
  RSS_URL,
)}`;

const OurVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clickedVideo, setClickedVideo] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const MAX_RETRIES = 3;
      let attempt = 0;
      let success = false;
      
      let vids = [];
  
      // First try to fetch data from API
      while (attempt < MAX_RETRIES && !success) {
        try {
          const res = await fetch(`${PROXY_URL}`);
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          const xmlString = await res.text();

          if (xmlString.includes('<title>Error 404 (Not Found)')) {
            throw new Error('404 Error: Resource not found');
          }  

          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
  
          const entries = Array.from(xmlDoc.getElementsByTagName('entry'));
  
          vids = entries.map((entry) => ({
            videoId: entry.getElementsByTagName('yt:videoId')[0]?.textContent,
            title: entry.getElementsByTagName('title')[0]?.textContent,
            link: entry.getElementsByTagName('link')[0]?.getAttribute('href'),
            published: entry.getElementsByTagName('published')[0]?.textContent,
            description: entry.getElementsByTagName('media:description')[0]?.textContent,
            views: entry.getElementsByTagName('media:community')[0]?.getElementsByTagName('media:statistics')[0]?.getAttribute('views'),
            author: entry.getElementsByTagName('author')[0]?.getElementsByTagName('name')[0]?.textContent,
            accountUrl: entry.getElementsByTagName('author')[0]?.getElementsByTagName('uri')[0]?.textContent,
          }));
  
          setVideos(vids);
          localStorage.setItem('Boostfloral-videos', JSON.stringify({ data: vids, timestamp: Date.now() }));
          success = true; // mark as successful to stop retrying
        } catch (err) {
          attempt++;
          console.error(`Attempt ${attempt} failed:`, err);
          if (attempt < MAX_RETRIES) {
            await new Promise((res) => setTimeout(res, 1000)); // wait 1s before retry
          }
        }
      }
  
      // If all retries fail, use localStorage as a fallback
      if (!success) {
        const storedData = localStorage.getItem('Boostfloral-videos');
        const currentTime = Date.now();
  
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const dataAge = currentTime - parsedData.timestamp;
  
          // If the cached data is older than 12 hours, discard it
          if (dataAge < 12 * 60 * 60 * 1000) {
            setVideos(parsedData.data); // Use cached data if within 12 hours
          } else {
            localStorage.removeItem('Boostfloral-videos');
            setVideos([]); // Clear videos if cache is expired
          }
        }
      }
  
      setLoading(false);
    };
  
    fetchData();
  }, []);
  

  const PostContent = ({ item }) => {
    const [showReadMore, setShowReadMore] = useState(false);
    const contentRef = useRef(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      const checkOverflow = () => {
        if (contentRef.current) {
          const content = contentRef.current;
          // Check if content overflows its container
          setShowReadMore(content.scrollHeight > content.clientHeight);
        }
      };

      // Run on mount and when item.message changes
      checkOverflow();

      // Optionally, re-check on window resize
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }, [item.description]);

    const toggleReadMore = () => {
      setExpanded(!expanded);
    };

    return (
      <div>
        <p
          ref={contentRef}
          style={{
            maxHeight: expanded ? '5000px' : '100px',
            overflow: 'hidden',
          }}
          dangerouslySetInnerHTML={{ __html: item.description }}
        />

        {showReadMore && (
          <span onClick={toggleReadMore}>
            <p style={{ cursor: 'pointer', color: '#9a9a9a' }}>
              {expanded ? 'Read Less' : 'Read More'}
            </p>
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <SeoHead data={seoData} />
      <div className="py-4">
        <div className="d-flex flex-column justify-content-center align-items-center mt-4">
          <h3
            style={{
              fontSize: '28px',
              fontWeight: '100',
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            Checkout our Youtube Channel
          </h3>
        </div>
        {loading ? (
          <div>
            <StyledLoading />
          </div>
        ) : (
          <div>
            <div className="d-flex flex-column justify-content-center align-items-center mb-2">
              <a
                href={`${videos[0]?.accountUrl}` ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer' }}
              >
                <StyledHeading>{`@${videos[0]?.author ?? 'BoostFloral'} `}</StyledHeading>
              </a>
            </div>
            <ResponsiveMasonry
              className="py-4 px-5"
              columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1000: 4 }}
            >
              <Masonry gutter="25px">
                {videos.map((item) => (
                  <Card style={{ backgroundColor: '#fff' }} key={item.id}>
                    {item?.videoId && (
                      <div
                        style={{
                          borderTopRightRadius: '5px',
                          borderTopLeftRadius: '5px',
                          width: '100%',
                          height: '260px',
                        }}
                        onClick={() => {
                          setClickedVideo((prevState) => [
                            ...prevState,
                            item?.videoId,
                          ]);
                        }}
                      >
                        {clickedVideo?.includes(item?.videoId) ? (
                          <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${item.videoId}?autoplay=1`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                              borderTopRightRadius: '5px',
                              borderTopLeftRadius: '5px',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer',
                              position: 'relative',
                              background: '#000000',
                              borderTopRightRadius: '5px',
                              borderTopLeftRadius: '5px',
                            }}
                          >
                            <img
                              style={{
                                borderTopRightRadius: '5px',
                                borderTopLeftRadius: '5px',
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderTopRightRadius: '5px',
                                borderTopLeftRadius: '5px',
                              }}
                              src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`} // YouTube thumbnail URL
                              alt="Video Thumbnail"
                            />
                            <span
                              className="d-flex justify-content-center align-items-center"
                              style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                              }}
                            >
                              <YoutubeIcon size={40} />
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <div className="px-3 d-flex flex-column my-2">
                       <div className='d-flex flex-row align-items-center'>
                       <div style={{ height: '100%', width: 'auto' }}>
                          <a
                            style={{
                              cursor: 'pointer',
                              height: '100%',
                              width: '100%',
                            }}
                            href={item?.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              style={{ height: '100%', width: '40px' }}
                              src={
                                'https://images.boostfloral.com/image-gallery/fba3cdff-0c6f-11ef-8ae8-12b0fba41a81/44d11782-8e77-46ca-85ea-d40645baa81b.jpg'
                              }
                            />
                          </a>
                        </div>

                        <div className="mx-2" style={{ flex: 1 }}>
                          <a
                            href={item?.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ cursor: 'pointer' , fontSize : '16px'}}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.textDecoration =
                                'underline')
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.textDecoration = 'none')
                            }
                          >
                            {item?.title}
                          </a>
                        </div>
                       </div>

                        <div className="d-flex flex-row mt-2 px-3">
                          <span className="d-flex flex-row">
                            <p style={{fontSize : '13px'}} className="">{item?.views}</p>
                            <p  style={{fontSize : '13px'}} className="mx-1">
                              {item?.views <= 1 ? 'View' : 'Views'}
                            </p>
                          </span>
                          <OverlayTrigger
                            overlay={
                              <StyledTooltip>
                                {formatDateTime(item.published)}
                              </StyledTooltip>
                            }
                          >
                            <p
                              className="mx-2"
                              style={{
                                width: 'fit-content',
                                cursor: 'pointer',
                                fontSize : '13px'
                              }}
                            >
                              {timeAgo2(item.published)}
                            </p>
                          </OverlayTrigger>
                        </div>
                      </div>

                      <hr style={{ marginTop: '1px' }} />
                      <div className="px-3 pb-3">
                        <PostContent item={item} />
                      </div>
                    </div>
                  </Card>
                ))}
              </Masonry>
            </ResponsiveMasonry>
          </div>
        )}
      </div>
      <style>
        {`
                                .ytp-chrome-top ytp-show-cards-title {
                                display : none !important;}
                                `}
      </style>
    </>
  );
};

export default OurVideos;
